/**
 * BL-2859 (ADR 076 — Money Input Text) — the one parser every money box and the
 * shared money schema use: typed text becomes a `Money` or a refusal code, and
 * the text itself is never repaired. Client-bundle-safe and pure.
 */
import Decimal from 'decimal.js';

import { isSupportedCurrency } from './currency';
import { exceedsMoneyCeiling, moneyCeiling } from './ceiling';
import { format, moneyInputProps } from './format';
import { Money } from './money';

/** Why typed text is not a legal amount. */
export type MoneyInputCode = 'not_a_number' | 'negative_not_allowed' | 'too_many_decimals' | 'out_of_range' | 'unsupported_currency';

/**
 * The field message for text that is not an amount at all. One source, so every
 * money field refuses malformed input in the same words (BL-2117).
 */
export const MONEY_INPUT_MALFORMED_MESSAGE = 'Enter an amount like 1234.50';

/** The message for a minus on a field that does not allow one. */
export const MONEY_INPUT_NEGATIVE_MESSAGE = "Can't be negative";

/** The message for a fraction longer than the field allows. */
export function tooManyDecimalsMessage(places: number): string {
    return `Use at most ${places} decimal places`;
}

/** The message for an amount past the currency's ceiling, named in that currency. */
export function outOfRangeMessage(currency: Parameters<typeof moneyCeiling>[0]): string {
    return `Enter an amount of at most ${format(moneyCeiling(currency))}`;
}

/** The result of {@link parseMoneyInput}. */
export type MoneyInputResult = { ok: true; money: Money } | { ok: false; code: MoneyInputCode; message: string };

/** A plain typed decimal: optional minus, digits with an optional point, or a bare fraction (".5"). */
const TYPED_DECIMAL_RE = /^-?(\d+\.?\d*|\.\d+)$/;

/**
 * Whether text is a plain decimal amount ("5", "5.", ".5", "-5.25"). The one
 * shape rule the box, the money schema and the wire decoder all judge by, so an
 * amount one accepts the others do too.
 *
 * @param text - The amount text, separators already removed.
 * @returns True for digits with an optional minus and point, and no exponent.
 */
export function isTypedDecimal(text: string): boolean {
    return TYPED_DECIMAL_RE.test(text);
}

/**
 * Remove what is not part of the amount: grouping commas, spaces and a pasted
 * currency sign. Nothing else changes: a minus, point or letter stays, so the
 * parser can refuse it by name rather than the box turning it into another number.
 *
 * @param text - The box's text.
 * @returns The text without separators and currency signs.
 */
export function normalizeTypedText(text: string): string {
    return text.replace(/[\s,$¥€£]/g, '');
}

/**
 * Turn typed text into `Money`, or the first reason it is not a legal amount.
 *
 * @param opts.text - The box's text as typed (grouping commas are fine).
 * @param opts.currency - The amount's ISO currency.
 * @param opts.allowNegative - Whether a leading minus is legal. Defaults to false.
 * @param opts.decimalPlaces - Most digits after the point. Defaults to the currency's minor unit.
 * @returns The amount, or a refusal with its code and the field message.
 * @sideEffect none — pure.
 */
export function parseMoneyInput(opts: { text: string; currency: string; allowNegative?: boolean; decimalPlaces?: number }): MoneyInputResult {
    const { currency } = opts;
    const text = normalizeTypedText(opts.text);
    if (!isTypedDecimal(text)) return refusal('not_a_number', MONEY_INPUT_MALFORMED_MESSAGE);
    if (!isSupportedCurrency(currency)) return refusal('unsupported_currency', 'Unsupported currency');
    const value = new Decimal(text);
    // `-0` is not below zero: a zero is a value (rule #4), and a zero-only rule is the field's own.
    if (value.lessThan(0) && !(opts.allowNegative ?? false)) return refusal('negative_not_allowed', MONEY_INPUT_NEGATIVE_MESSAGE);
    // The ceiling bounds the size of the amount, whichever way it points.
    if (exceedsMoneyCeiling(Money.of(value.abs(), currency))) return refusal('out_of_range', outOfRangeMessage(currency));
    const places = opts.decimalPlaces ?? moneyInputProps(currency).decimalPlaces;
    if (value.decimalPlaces() > places) return refusal('too_many_decimals', tooManyDecimalsMessage(places));
    return { ok: true, money: Money.of(value, currency) };
}

function refusal(code: MoneyInputCode, message: string): MoneyInputResult {
    return { ok: false, code, message };
}
